// web/src/screens/RequestDetail.tsx — one update task (CHALICE_TASKS_SPEC v2 §5 C2 +
// 22_PREVIEW_AND_PUBLISH.md): the 6-step rail (Received → Questions → Building → Checking →
// Preview → Live), the chat thread that drives clarification + tweaks, the preview card with
// "Make it live" (+ confirm), the honest publish progress card, and "Roll back" for 7 days after
// go-live. Polling keeps it live; the spec-approval gate / AC matrix / PR card are gone.
//
// Portal spec 19: requests now go to the update agent. "Make it live" is no longer chalice-only (the agent's
// preview publishes the same way), and the four new turns get plain-words cards: `waiting` (another change first),
// `publish_waiting` (going live right after the change ahead), `set_aside` (reply to pick it back up) and
// `queued_for_update` (photos kept for the next monthly update). Roll back stays with chalice.
//
// Two things this screen must never do (doc 22 §2, doc 20 §7 item 9):
//   - offer Publish before the gate is green. `preview_ready` is the ONLY state with the button,
//     and applyGateResult() is the only thing that writes it.
//   - show a preview the gate is still looking at. `verifying` and `needs_attention` both have a
//     previewUrl on the row and neither may render it.
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { AsyncBoundary } from '../components/AsyncBoundary';
import { Skeleton } from '../components/Skeleton';
import { StatusBadge } from '../components/StatusBadge';
import { RequestRail } from '../components/StageRail';
import { PublishProgress } from '../components/PublishProgress';
import { Icon } from '../components/Icon';
import { toast } from '../components/Toast';
import { FEATURES } from '../lib/features';
import { apiErrorText } from '../lib/errors';

const ROLE_LABEL: Record<string, string> = { customer: 'You', chalice: 'Us', assistant: 'Us', system: '' };

// Statuses whose preview is deliberately hidden: the gate is either still looking at it or has
// said it is not good enough to show. The row HAS a previewUrl in both cases.
const PREVIEW_WITHHELD = ['verifying', 'needs_attention'];

export function RequestDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['run', id], queryFn: () => api.getRun(id), refetchInterval: 4000 });
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<null | 'live' | 'rollback'>(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ['run', id] }); qc.invalidateQueries({ queryKey: ['home'] }); qc.invalidateQueries({ queryKey: ['requests'] }); };

  return (
    <AsyncBoundary query={q} skeleton={<Skeleton variant="card" count={3} />}>
      {(run) => {
        const closed = run.status === 'archived' || run.status === 'rejected';
        // Photo-only rows and set-aside asks have no build to follow: no rail.
        const noRail = closed || run.status === 'queued_for_update' || run.status === 'set_aside';
        async function send() {
          if (!draft.trim()) return;
          setBusy(true);
          try {
            await api.sendMessage(run.id, draft.trim());
            setDraft('');
            toast({ kind: 'success', message: 'Sent' });
          } finally { setBusy(false); }
          refresh();
        }
        async function makeLive() {
          setBusy(true);
          try {
            const r = await api.approveRequest(run.id);
            toast({ kind: 'success', message: r.status === 'publish_waiting' ? 'Going live right after the change ahead of it.' : 'Publishing your update…' });
          } catch (e) {
            const m = apiErrorText(e, '');
            toast({ kind: 'error', message: /another change is in progress/.test(m)
              ? 'Another change is going on your site right now — try again shortly.'
              : "Couldn't publish — our team has been alerted." });
          } finally { setBusy(false); setConfirming(null); }
          refresh();
        }
        async function rollback() {
          setBusy(true);
          try {
            await api.rollbackRequest(run.id);
            toast({ kind: 'success', message: 'Rolling back…' });
          } finally { setBusy(false); setConfirming(null); }
          refresh();
        }
        // doc 22 §2: the undo closes 7 days after go-live. The server computes the deadline and
        // enforces it, so the button and the endpoint can never disagree — an offered button that
        // 400s is worse than no button. A response with no deadline hides it.
        const canRollBack =
          run.status === 'live' &&
          run.rollbackUntil != null &&
          Date.now() / 1000 <= run.rollbackUntil;
        const showPreview = !!run.previewUrl && !PREVIEW_WITHHELD.includes(run.status);
        return (
          <>
            <Link to="/requests" className="mb-4 inline-flex min-h-11 items-center gap-1 text-sm text-muted hover:text-ink"><Icon name="arrow-left" size={16} />Requests</Link>
            <PageHeader
              title={run.displayNo ? `Update #${run.displayNo} — ${run.title}` : run.title}
              subtitle={run.siteName}
              action={<StatusBadge status={run.status} />}
            />

            {/* Stage rail — always visible, progress saved (§4a) */}
            {!noRail && (
              <div className="rounded-lg border border-hair bg-surface p-4">
                <RequestRail status={run.status} hasPreview={!!run.previewUrl} />
              </div>
            )}

            {/* Portal spec 19: the update agent's turns, in plain words. Nothing here is a problem to solve. */}
            {run.status === 'waiting' && (
              <Section title="Up next">
                <p className="text-sm text-ink">We're finishing another change on your site. Yours starts right after — nothing you need to do.</p>
              </Section>
            )}
            {run.status === 'publish_waiting' && (
              <Section title="Going live next" accent="run">
                <p className="text-sm text-ink">You've said yes. It goes live right after the change ahead of it — nothing you need to do.</p>
              </Section>
            )}
            {run.status === 'set_aside' && (
              <Section title="Set aside">
                <p className="text-sm text-ink">We set this aside so your site can keep improving. Reply below any time to pick it back up.</p>
              </Section>
            )}
            {run.status === 'queued_for_update' && (
              <Section title="Added to your next monthly update">
                <p className="text-sm text-ink">Thanks! We'll use these in your next monthly update.</p>
                <Link to={`/request${run.siteId ? `?site=${run.siteId}` : ''}`}
                  className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-sm text-ink">
                  <Icon name="send" size={15} />Want them sooner? Ask for a change
                </Link>
              </Section>
            )}

            {/* Calm failure copy (§4a): the live site is never touched by a failure. §8 makes the
                publish case explicit — Amplify keeps serving the last good build, so "your site is
                unchanged" is literally true and is the reassuring thing to say. */}
            {run.status === 'failed' && (
              <Section title="We hit a snag" accent="warn">
                <p className="text-sm text-ink">
                  {run.publishStartedAt
                    ? 'Something went wrong publishing this update — we\'re on it. Your live site is unchanged.'
                    : "We hit a snag building this update — we're on it. Your live site is not affected."}
                </p>
              </Section>
            )}

            {/* The QA gate leg (doc 20). "Checking" is all the customer is told — never a check
                name, never a count, never that anything was automatically repaired. */}
            {run.status === 'verifying' && (
              <Section title="Checking your update">
                <p className="text-sm text-ink">
                  We're giving this a once-over — phones, links, images — before you see it. Usually
                  a minute or two.
                </p>
              </Section>
            )}
            {run.status === 'needs_attention' && (
              <Section title="We're taking a closer look" accent="warn">
                <p className="text-sm text-ink">
                  We want this right before you see it, so someone on our side is looking at it now.
                  We'll be in touch shortly — your live site is not affected.
                </p>
              </Section>
            )}
            {/* Doc 11's soft stop (02 §5), given a customer-facing explanation by doc 12 §3.
                Credits, never dollars.

                §3's "Add credits" button, restored. Phase A omitted it and said so: there was no
                Stripe rail to send anyone to, and nothing re-enqueued a parked row even if credits
                arrived (90_OPEN_ITEMS D-5), so a button would have been a dead end dressed as an
                action. Both are closed now — the packs are on /billing and the top-up webhook
                releases this row oldest-first — so the banner can promise the restart. The chat
                below still reaches a human, which is why no copy here tells anyone to use it. */}
            {run.status === 'awaiting_credits' && (
              <Section title="Waiting for credits" accent="warn">
                <p className="text-sm text-ink">
                  We'll start this as soon as you have credits. Nothing is lost — it's saved
                  exactly as you wrote it, and it starts on its own when you add credits or your
                  month resets.
                </p>
                <Link to="/billing"
                  className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-md bg-run px-4 text-sm font-medium text-runink">
                  <Icon name="credit-card" size={15} />Add credits
                </Link>
              </Section>
            )}

            {/* The publish wait, made legible (doc 22 §3). Capped at 95% until the poller says live. */}
            {run.status === 'merging' && (
              <PublishProgress startedAt={run.publishStartedAt} expectedMs={run.expectedPublishMs} />
            )}
            {run.status === 'rejected' && (
              <Section title="We can't make this change" accent="warn">
                <p className="text-sm text-ink">This one's outside what updates can change — the chat below explains why. If you think that's wrong, just ask for something a little different.</p>
              </Section>
            )}

            {/* The ask, without the raw photo addresses: "Uploaded image: <address>" lines are for the update agent
                (portal spec 19); the client sees how many photos came with it. */}
            <Section title="The request">
              {(() => {
                const lines = run.ticketBody.split(/\r?\n/);
                const isPhoto = (l: string) => /^Uploaded image: /.test(l.trim());
                const photos = lines.filter(isPhoto).length;
                const text = lines.filter((l) => !isPhoto(l)).join('\n').trim();
                return (
                  <>
                    {text && <p className="whitespace-pre-wrap text-sm text-muted">{text}</p>}
                    {photos > 0 && (
                      <p className={`inline-flex items-center gap-1.5 text-sm text-ink ${text ? 'mt-2' : ''}`}>
                        <Icon name="image" size={15} />{photos === 1 ? '1 photo attached' : `${photos} photos attached`}
                      </p>
                    )}
                  </>
                );
              })()}
            </Section>

            {/* Preview card (§5 C2) — persists after live (the preview URL equals production after
                merge). Withheld while the gate has it (doc 20): the row carries a previewUrl from
                the moment the build deployed, and showing it before the gate is green is exactly
                the "told ready before anything verified it" problem doc 22 §1 exists to remove. */}
            {showPreview && (
              <Section title="Your preview" accent={run.status === 'preview_ready' ? 'run' : undefined}>
                {run.changeSummary && <p className="mb-3 text-sm text-ink">{run.changeSummary}</p>}
                <div className="flex flex-wrap items-center gap-2">
                  <a href={run.previewUrl ?? undefined} target="_blank" rel="noreferrer"
                    className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-sm text-ink">
                    <Icon name="external" size={15} />Open preview
                  </a>
                  {/* "Make it live" publishes the update agent's preview (portal spec 19). Roll back drives chalice's
                      revert, so it stays hidden with chalice (spec 10); to undo a change, the client asks for one. */}
                  {run.status === 'preview_ready' && (confirming === 'live' ? (
                    <span className="inline-flex flex-wrap items-center gap-2">
                      <span className="text-sm text-muted">{FEATURES.chalice
                        ? 'This publishes your preview to your live site. You can undo it afterwards.'
                        : 'This puts the preview on your live site. Want it changed back later? Just ask.'}</span>
                      <button onClick={makeLive} disabled={busy}
                        className="inline-flex min-h-11 items-center gap-2 rounded-md bg-run px-4 text-sm font-medium text-runink disabled:opacity-50">
                        <Icon name="rocket" size={15} />Yes — publish
                      </button>
                      <button onClick={() => setConfirming(null)} className="min-h-11 rounded-md border border-hair px-3 text-sm text-ink">Cancel</button>
                    </span>
                  ) : (
                    <button onClick={() => setConfirming('live')} disabled={busy}
                      className="inline-flex min-h-11 items-center gap-2 rounded-md bg-run px-4 text-sm font-medium text-runink disabled:opacity-50">
                      <Icon name="rocket" size={15} />Make it live
                    </button>
                  ))}
                  {FEATURES.chalice && canRollBack && (confirming === 'rollback' ? (
                    <span className="inline-flex flex-wrap items-center gap-2">
                      <span className="text-sm text-muted">This puts your live site back exactly as it was before this update.</span>
                      <button onClick={rollback} disabled={busy}
                        className="inline-flex min-h-11 items-center gap-2 rounded-md border border-danger px-4 text-sm text-danger disabled:opacity-50">
                        <Icon name="refresh" size={15} />Yes — roll back
                      </button>
                      <button onClick={() => setConfirming(null)} className="min-h-11 rounded-md border border-hair px-3 text-sm text-ink">Cancel</button>
                    </span>
                  ) : (
                    <button onClick={() => setConfirming('rollback')} disabled={busy}
                      className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-sm text-muted hover:text-ink disabled:opacity-50">
                      <Icon name="refresh" size={15} />Roll back
                    </button>
                  ))}
                </div>
              </Section>
            )}

            {/* The chat (§4b) — clarifications, tweaks, and questions about THIS update. */}
            <Section title="Chat" accent={run.status === 'clarifying' ? 'warn' : undefined}>
              {run.messages.length === 0 && (
                <p className="mb-3 text-sm text-muted">No messages yet — we'll post here if we have a question, and you can add details any time.</p>
              )}
              <div className="flex flex-col gap-2">
                {run.messages.map((m) => (
                  m.role === 'system' ? (
                    <div key={m.id} className="py-1 text-center text-xs text-muted">{m.body}</div>
                  ) : (
                    <div key={m.id}
                      className={`max-w-[85%] rounded-lg border border-hair p-3 text-sm ${m.role === 'customer' ? 'self-end bg-surface2 text-ink' : 'self-start bg-bg text-ink'}`}>
                      <div className="mb-1 font-head text-[10px] text-muted">{ROLE_LABEL[m.role] ?? m.role}</div>
                      <p className="whitespace-pre-wrap">{m.body}</p>
                    </div>
                  )
                ))}
                {/* What this update cost, in the thread where the work happened (doc 12 §2/§3).
                    There is no billing-history page and there is not going to be one: a
                    per-request ledger at this price point is a support-ticket generator.
                    Rendered from `creditsCharged`, which IS `requests.credits_charged` — the
                    server settles once at a terminal state, so this appears exactly once and
                    cannot disagree with what was billed. Never a dollar figure (02 §2). */}
                {typeof run.creditsCharged === 'number' && run.creditsCharged > 0 && (
                  <div className="py-1 text-center text-xs text-muted">
                    This update used {run.creditsCharged.toLocaleString()}{' '}
                    {run.creditsCharged === 1 ? 'credit' : 'credits'}.
                  </div>
                )}
              </div>
              {!closed && (
                <div className="mt-3">
                  <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3}
                    className="w-full rounded-md border border-hair bg-bg px-3 py-2.5 text-sm text-ink"
                    placeholder={run.status === 'clarifying' ? 'Type your answer…' : run.status === 'preview_ready' ? 'Want a tweak? Describe it…' : 'Add details…'} />
                  <button onClick={send} disabled={busy || !draft.trim()}
                    className="mt-2 inline-flex min-h-11 items-center gap-2 rounded-md bg-run px-4 text-sm font-medium text-runink disabled:opacity-50">
                    <Icon name="send" size={15} />Send
                  </button>
                </div>
              )}
            </Section>
          </>
        );
      }}
    </AsyncBoundary>
  );
}

function Section({ title, accent, children }: { title: string; accent?: 'warn' | 'run'; children: ReactNode }) {
  const border = accent === 'warn' ? 'border-l-4 border-l-warn' : accent === 'run' ? 'border-l-4 border-l-run' : '';
  return (
    <section className={`mt-5 rounded-lg border border-hair ${border} bg-surface p-4`}>
      <h2 className="mb-2 font-head text-sm text-muted">{title}</h2>
      {children}
    </section>
  );
}
